import React, { useEffect, useRef, useCallback } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { IoIosArrowForward } from "react-icons/io";
import { BsFillGridFill } from "react-icons/bs";
import { FaThList } from "react-icons/fa";
import ShopProducts from "../components/products/ShopProducts";
import { useDispatch, useSelector } from "react-redux";
import { price_range_product, query_ofertas } from "../store/reducers/homeReducer";
import { Helmet } from "react-helmet";
import { PropagateLoader } from "react-spinners";

import { useFilterState } from "../hooks/useFilterState";
import { useTheme } from "../context/ThemeContext";
import {
  CatalogActiveFilters,
  CatalogFilterPanel,
  CatalogFilterToggleButton,
  FILTER_ARRAY_KEYS,
  asFilterArray,
  hasActiveCatalogFilters,
} from "../components/global/ProductFilters";

const Ofertas = () => {
  const dispatch = useDispatch();
  const { getColor } = useTheme();
  const { currentCompany } = useSelector(state => state.init);
  const { ofertaProducts: products, totalOfertas: totalProduct, categorys, typeproducts, typearticles, brands, colors, sizes, priceRange } = useSelector(state => state.home);
  const primaryColor = getColor("primary", "#ae1d22");
  const filterOptions = { categorys, typeproducts, typearticles, brands, colors, sizes };

  const { filters, expandedSections, updateFilter, handleCheckboxChange, handlePriceChange, toggleSection, resetFilters } = useFilterState(
    {},
    { priceRange }
  );

  // Refs scroll infinito
  const sentinelRef = useRef(null);
  const loadingRef = useRef(false);
  const filtersRef = useRef(filters);
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  const hasMore = products.length < totalProduct;

  const bgStyle = { backgroundImage: `url(${currentCompany?.imageDetails})` };

  useEffect(() => { dispatch(price_range_product()); }, [dispatch]);

  // Reset página al cambiar filtros
  const priceValue0 = filters.priceValues[0];
  const priceValue1 = filters.priceValues[1];
  useEffect(() => {
    updateFilter("pageNumber", 1);
    loadingRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.category, filters.typeproduct, filters.typearticle, filters.brand,
    filters.color, filters.size, filters.sortPrice,
    priceValue0, priceValue1
  ]);

  // Cargar ofertas con estado separado
  useEffect(() => {
    dispatch(query_ofertas({
      low: filters.priceValues[0],
      high: filters.priceValues[1],
      category: filters.category,
      typeproduct: filters.typeproduct,
      typearticle: filters.typearticle,
      brand: filters.brand,
      color: filters.color,
      size: filters.size,
      sortPrice: filters.sortPrice,
      pageNumber: filters.pageNumber,
    }));
    loadingRef.current = false;
  }, [dispatch, filters]);

  // IntersectionObserver
  const loadMore = useCallback(() => {
    if (loadingRef.current || products.length >= totalProduct) return;
    loadingRef.current = true;
    updateFilter("pageNumber", filtersRef.current.pageNumber + 1);
  }, [products.length, totalProduct, updateFilter]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  const clearAllFilters = () => {
    resetFilters();
    if (priceRange?.low && priceRange?.high) handlePriceChange([priceRange.low, priceRange.high]);
  };

  const removeFilter = useCallback((filterKey, value = "") => {
    if (FILTER_ARRAY_KEYS.includes(filterKey)) {
      const currentValues = asFilterArray(filtersRef.current[filterKey]);
      updateFilter(filterKey, value ? currentValues.filter(item => item !== value) : []);
    } else {
      updateFilter(filterKey, "");
    }

    if (filterKey === "priceValues" && priceRange) {
      handlePriceChange([priceRange.low, priceRange.high]);
    }
  }, [updateFilter, handlePriceChange, priceRange]);

  const hasActiveFilters = hasActiveCatalogFilters(filters, priceRange);

  return (
    <div>
      <Header />
      <Helmet>
        <title>Ofertas | Tekos.pe</title>
        <meta name="description" content="Encuentra las mejores ofertas y descuentos en zapatillas y calzado deportivo." />
      </Helmet>

      {currentCompany?.verimageDetails && (
        <section className="h-[220px] mt-6 bg-cover bg-no-repeat relative bg-left" style={bgStyle}>
          <div className="absolute left-0 top-0 w-full h-full bg-[#2422228a]">
            <div className="w-[85%] md:w-[80%] sm:w-[90%] lg:w-[90%] h-full mx-auto">
              <div className="flex flex-col justify-center gap-1 items-center h-full w-full text-white">
                <h2 className="text-3xl font-bold">Ofertas</h2>
                <div className="flex justify-center items-center gap-2 text-2xl w-full">
                  <Link to="/" className="hover:text-gray-200 transition-colors">Inicio</Link>
                  <span className="pt-1"><IoIosArrowForward /></span>
                  <span>Ofertas</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-8">
        <div className="w-[85%] md:w-[80%] sm:w-[90%] lg:w-[90%] h-full mx-auto">

          <CatalogActiveFilters
            filters={filters}
            priceRange={priceRange}
            onRemove={removeFilter}
            onClear={clearAllFilters}
          />
          {/* Boton filtros movil */}
          <div className="md:block hidden mb-6">
            <CatalogFilterToggleButton
              isOpen={filters.filterVisible}
              onClick={() => updateFilter("filterVisible", !filters.filterVisible)}
              hasActiveFilters={hasActiveFilters}
            />
          </div>
          <div className="w-full flex flex-wrap">
            {/* Filtros */}
            <div className={`w-3/12 md-lg:w-4/12 md:w-full pr-8 ${filters.filterVisible ? "md:hidden" : ""}`}>
              <CatalogFilterPanel
                filterOptions={filterOptions}
                filters={filters}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
                onCheckboxChange={handleCheckboxChange}
                priceRange={priceRange}
                onPriceChange={handlePriceChange}
                idPrefix="ofertas"
              />
            </div>
            {/* Productos */}
            <div className="w-9/12 md-lg:w-8/12 md:w-full">
              <div className="pl-8 md:pl-0">
                <div className="py-4 bg-white mb-6 px-6 rounded-lg flex justify-between items-center border border-gray-200 shadow-sm">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-800">
                      {totalProduct} Oferta{totalProduct !== 1 ? "s" : ""} disponible{totalProduct !== 1 ? "s" : ""}
                    </h2>
                    {hasActiveFilters && <p className="text-sm text-gray-500 mt-1">Filtradas según tu selección</p>}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <select
                        onChange={e => updateFilter("sortPrice", e.target.value)}
                        value={filters.sortPrice}
                        className="p-2.5 pl-10 border border-gray-300 outline-0 text-slate-700 font-medium rounded-lg appearance-none bg-white pr-10"
                        style={{ borderColor: filters.sortPrice ? primaryColor : undefined }}
                        aria-label="Ordenar productos">
                        <option value="">Ordenar por</option>
                        <option value="low-to-high">Precio: Bajo a Alto</option>
                        <option value="high-to-low">Precio: Alto a Bajo</option>
                      </select>
                    </div>
                    <div className="flex gap-2 md-lg:hidden bg-gray-100 p-1 rounded-lg">
                      <button onClick={() => updateFilter("styles", "grid")}
                        className={`p-2.5 rounded-md transition-all ${filters.styles === "grid" ? "bg-white shadow-sm" : "text-gray-500"}`}
                        style={filters.styles === "grid" ? { color: primaryColor } : undefined}>
                        <BsFillGridFill size={18} />
                      </button>
                      <button onClick={() => updateFilter("styles", "list")}
                        className={`p-2.5 rounded-md transition-all ${filters.styles === "list" ? "bg-white shadow-sm" : "text-gray-500"}`}
                        style={filters.styles === "list" ? { color: primaryColor } : undefined}>
                        <FaThList size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="pb-8">
                  {products?.length > 0 ? (
                    <ShopProducts products={products} styles={filters.styles} />
                  ) : (
                    <div className="text-center py-16">
                      <h3 className="text-2xl font-semibold text-gray-700 mb-3">No hay ofertas disponibles</h3>
                      <p className="text-gray-500 mb-6">No hay productos con descuento activo en este momento.</p>
                      {hasActiveFilters && (
                        <button onClick={clearAllFilters} className="px-6 py-3 text-white font-medium rounded-lg transition-opacity hover:opacity-90" style={{ backgroundColor: primaryColor }}>
                          Limpiar filtros
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div ref={sentinelRef} className="w-full h-4" />
                {hasMore && (
                  <div className="flex justify-center py-6">
                    <PropagateLoader color={primaryColor} size={12} />
                  </div>
                )}
                {!hasMore && products.length > 0 && (
                  <p className="text-center text-gray-400 text-sm py-6">
                    — {totalProduct} oferta{totalProduct !== 1 ? "s" : ""} mostrada{totalProduct !== 1 ? "s" : ""} —
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Ofertas;
